import { useEffect, useRef, useState } from 'preact/hooks'
import { useAthosContext } from 'libs/athos/fetchers/provider'
import { AthosSuggestProvider } from 'libs/athos/fetchers/suggest'
import { AthosTrendingProvider } from 'libs/athos/fetchers/trending'
import { SearchPageLink } from 'libs/athos/components/quick-search/SearchPageLink'
import { QuickSearchSuggestions } from 'libs/athos/components/quick-search/QuickSearchSuggestions'
import { QuickSearchResults } from 'libs/athos/components/quick-search/QuickSearchResults'
import clsx from 'clsx'
import { globalState } from '@/components/helpers/utils'
import { SkeletonCard } from 'libs/athos/components/SkeletonCard'
import { useSearchResultState } from '../../hooks/useSearchResultState'
import { calculateProductCount } from 'libs/athos/components/collection/filters/ProductCount'

/**
 * Gets popular categories HTML content from the DOM
 * @returns {string|undefined} HTML content for popular categories or undefined if not found
 */
const getPopularCategories = () => {
  const popularCategoryWrapper = document.getElementById('popular-categories')
  return popularCategoryWrapper?.innerHTML || undefined
}

// Extract and filter default suggestions from global state
const defaultSuggestions = globalState.search?.suggestions || []
const collectionsSuggestions = defaultSuggestions.filter(s => s.type === 'collections') || []
const pagesSuggestions = defaultSuggestions.filter(s => s.type === 'pages') || []

// Configuration for different search states
export const SEARCH_CONFIG = {
  NO_SEARCH: {
    suggestions: [
      { title: 'Popular Searches', type: 'collections', default: collectionsSuggestions, isStatic: true },
      { title: 'Popular Pages', type: 'pages', default: pagesSuggestions, isStatic: true }
    ],
    mainTitle: 'Popular Categories'
  },
  NO_RESULTS: {
    suggestions: [
      { title: 'Popular Searches', type: 'collections', default: collectionsSuggestions },
      { title: 'Popular Pages', type: 'pages', default: pagesSuggestions, isStatic: true }
    ],
    // A query with no results falls back to the same static category tiles as the
    // empty-input state; there is no popular-products carousel.
    mainTitle: 'Popular Categories'
  },
  HAS_RESULTS: {
    suggestions: [
      { title: 'Suggested Searches', type: 'collections', default: collectionsSuggestions },
      { title: 'Popular Pages', type: 'pages', default: pagesSuggestions, isStatic: true }
    ],
    mainTitle: 'Search Results'
  },
  REDIRECT: {
    mainTitle: 'Suggested Pages'
  }
}

/**
 * ViewResultsButton component - Renders the "View Results" button for search results
 * @param {Object} props - Component props
 * @param {string} props.query - Search query string
 * @param {number} props.totalResults - Total number of search results
 * @param {boolean} props.isMobile - Whether to show mobile-specific styling
 * @returns {JSX.Element} Rendered component
 */
export const ViewResultsButton = ({ query, totalResults, isMobile = false }) => {
  if (!query || !totalResults) return null

  const className = isMobile
    ? 'lg:!hidden btn btn-primary btn-fill btn-m w-full'
    : 'shrink-0 lg-max:!hidden btn btn-primary btn-fill btn-s'

  return (
    <SearchPageLink query={query} className={className}>
      <span>
        View {totalResults} Results
      </span>
    </SearchPageLink>
  )
}

/**
 * QuickSearchDropdown component - Displays search results and suggestions based on query
 * @param {Object} props - Component props
 * @param {string} props.query - Search query string
 * @returns {JSX.Element} Rendered component
 */
export function QuickSearchDropdown({ query, redirect, setRedirect, onResultsUpdate }) {
  const popularCategories = useRef(getPopularCategories())
  const { refine, results, isLoading } = useAthosContext()
  const { title, hasResults, showNoResults, isCheckingResults, isResultsReady } = useSearchResultState({ query, results, isLoading })

  // The no-results state settles on a short delay so it can't flicker between
  // keystrokes — the message and the category tiles share that one condition.
  const isNoResultsState = Boolean(query) && !hasResults && !isLoading && showNoResults
  // Popular categories back both the empty input and a query that found nothing,
  // so a single instance is toggled rather than a second copy of the markup.
  const showPopularCategories = !query || isNoResultsState

  const itemClass = 'w-grid-7/8 lg:w-full max-w-[280px] lg:max-w-grid-1/4 mr-gutter [_&>div]:w-full'
  const carouselSettings = {
    draggable: true,
    freeScroll: false,
    wrapAround: false,
    groupCells: true,
    fade: false,
    adaptiveHeight: false,
    accessibility: true,
    cellAlign: 'left',
    prevNextButtons: false,
    pageDots: false
  }

  // Update search query when query prop changes
  useEffect(() => {
    refine('query', query)
  }, [query])

  useEffect(() => {
    if (results?.merchandising?.redirect) {
      setRedirect(results?.merchandising?.redirect)
    } else {
      setRedirect(false)
    }
  }, [results?.merchandising?.redirect])

  useEffect(() => {
    if (onResultsUpdate && results) {
      const totalResults = calculateProductCount(results)
      const hasResults = totalResults > 0
      onResultsUpdate({ hasResults, totalResults })
    }
  }, [results, onResultsUpdate])

  /**
   * Renders suggestion components based on current search state
   * @returns {JSX.Element[]} Array of SearchSuggestions components
   */
  const renderSuggestions = () => {
    let config
    if (query && hasResults) {
      config = SEARCH_CONFIG.HAS_RESULTS
    } else if (query && !hasResults) {
      config = SEARCH_CONFIG.NO_RESULTS
    } else {
      config = SEARCH_CONFIG.NO_SEARCH
    }
    const suggestionQuery = query && !hasResults ? '' : query
    return config.suggestions.map((suggestion, index) => {
      return (
        <SearchSuggestions
          key={index}
          title={suggestion.title}
          type={suggestion.type}
          query={suggestionQuery}
          defaultSuggestions={suggestion.default}
          isStatic={suggestion.isStatic}
        />
      )
    })
  }

  /**
   * Renders the main content section based on search results
   * @returns {JSX.Element|null} QuickSearchResults or PopularCategories component
   */
  return (
    <div className="flex flex-col lg:grid lg:grid-cols-main gap-x-gutter lg:gap-y-1.5 max-h-[75vh] bg-secondary lg:px-margin overflow-y-scroll no-scrollbar overscroll-y-none">
      {/* Main Content Section */}
      <div className={clsx(
        'shrink-0 lg:col-[3/-1] lg:row-[1/2] flex flex-col lg:gap-y-0.5 w-full lg:w-full-margin-half h-fit px-0 lg:pl-2 pb-1 lg:py-2.5 overflow-x-hidden relative',
        !query && 'order-2 lg:order-none'
      )}>
        <div className={clsx(
          'flex items-center justify-between gap-x-gutter w-full px-margin lg:pr-margin lg:pl-0',
          query && 'py-1 lg:py-0'
        )}>

          <div className="flex flex-col gap-y-1 w-full">
            {isNoResultsState && (
              <div className="w-full pb-1 border-b border-b-borders-default">
              <span className="w-full paragraph-s font-medium bg-transparent">No results for "{query}", please try another search</span>
              </div>
            )}
            <h2 className="heading-m font-serif lg-max:hidden">{title}</h2>
              {(hasResults && !redirect) && (
                <div className="w-full">
                  <ViewResultsButton query={query} totalResults={calculateProductCount(results)} isMobile={true} />
                </div>
              )}
          </div>

          {(hasResults && !redirect) && (
            <ViewResultsButton query={query} totalResults={calculateProductCount(results)} isMobile={false} />
          )}
        </div>

        <div className="w-full">
          {/* Loading Overlay */}
          <div className={clsx('w-full overflow-hidden', (isCheckingResults || isLoading) ? '' : 'h-0 opacity-0 pointer-events-none')}>
            <SkeletonGrid carouselSettings={carouselSettings} itemClass={itemClass} />
          </div>
          <div className={clsx('w-full', (hasResults) ? '' : 'hidden')}>
            {isResultsReady && (
              <QuickSearchResults />
            )}
          </div>
          <div className={clsx('w-full', showPopularCategories ? '' : 'hidden')}>
            {popularCategories.current && <PopularCategories content={popularCategories.current} carouselSettings={carouselSettings} />}
          </div>
        </div>

      </div>

      {/* Suggestions Section */}
      <div className={clsx(
        'shrink-0 lg:col-[1/3] lg:row-[1/2] lg-max:p-margin bg-page-primary',
        !query && 'order-1 lg:order-none'
      )}>
      <div className="grid grid-cols-2 lg:flex lg:flex-col gap-0.5 lg:gap-y-2.5 w-full lg:w-full-margin-half h-fit lg:h-full px-margin lg:-ml-margin py-1 lg:py-2.5 lg:pr-1.75 bg-page-secondary">
        {renderSuggestions()}
      </div>
      </div>
    </div>
  )
}

/**
 * PopularCategories component - Renders HTML content for popular categories
 * @param {Object} props - Component props
 * @param {string} props.content - HTML content to render
 * @returns {JSX.Element} Rendered component
 */
const PopularCategories = ({ content, carouselSettings }) => {
  return (
    <>
      {/* Mobile: 2x2 grid. Injected items carry fixed carousel-peek widths (w-grid-7/8, mr-gutter) —
          override them via descendant selectors since we don't control the markup here. */}
      <div
        className="grid grid-cols-2 items-start gap-x-gutter gap-y-1 w-full lg:hidden [&>a]:w-full [&>a]:max-w-none [&>a]:mr-0 px-margin"
        dangerouslySetInnerHTML={{ __html: content }}
      />

      {/* Desktop: carousel */}
      <x-carousel
        className="hidden lg:flex w-full h-fit"
        carousel-settings={JSON.stringify(carouselSettings)}
        dangerouslySetInnerHTML={{ __html: content }}
      />
    </>
  )
}

/**
 * SkeletonGrid component - Renders a loading state with skeleton cards
 * @param {Object} props - Component props
 * @param {Object} props.carouselSettings - Settings for the carousel
 * @param {string} props.itemClass - CSS class for carousel items
 * @returns {JSX.Element} Rendered component
 */
const SkeletonGrid = ({ carouselSettings, itemClass }) => {
  return (
    <>
      {/* Mobile: 2x2 grid, matches QuickSearchResults */}
      <div className="grid grid-cols-2 items-start gap-x-gutter gap-y-1 w-full lg:hidden">
        <SkeletonCard className="w-full" />
        <SkeletonCard className="w-full" />
        <SkeletonCard className="w-full" />
        <SkeletonCard className="w-full" />
      </div>

      {/* Desktop: carousel */}
      <x-carousel
        className="hidden lg:flex w-full h-fit"
        carousel-settings={JSON.stringify(carouselSettings)}
      >
        <SkeletonCard className={itemClass} />
        <SkeletonCard className={itemClass} />
        <SkeletonCard className={itemClass} />
        <SkeletonCard className={itemClass} />
      </x-carousel>
    </>
  )
}

/**
 * SearchSuggestions component - Renders search suggestions based on query
 * @param {Object} props - Component props
 * @param {string} props.query - Search query string
 * @param {string} props.type - Type of suggestions ('collections' or 'pages')
 * @param {string} props.title - Title text for the suggestions section
 * @param {Array} props.defaultSuggestions - Default suggestions to display
 * @returns {JSX.Element} Rendered component
 */
const SearchSuggestions = ({ query, type, title, defaultSuggestions, isStatic = false }) => {
  const [isEmpty, setIsEmpty] = useState(false)

  return (
    <div className={clsx(
      'flex flex-col gap-y-0.75 relative after:content-blank after:absolute after:bottom-0 after:left-0 after:w-full lg:after:w-[68%] after:h-px after:bg-page-secondary pb-2.125 last:after:hidden last:pb-0',
      isEmpty && 'hidden'
    )}>
      <h3 className="heading-m font-serif">{title}</h3>
      {isStatic ? (
        <QuickSearchSuggestions query={query} defaultSuggestions={defaultSuggestions} onEmptyChange={setIsEmpty} />
      ) : (
        query ? (
        <AthosSuggestProvider category={type}>
          <QuickSearchSuggestions query={query} defaultSuggestions={defaultSuggestions} onEmptyChange={setIsEmpty} />
        </AthosSuggestProvider>
        ) : (
        <AthosTrendingProvider category={type}>
          <QuickSearchSuggestions defaultSuggestions={defaultSuggestions} onEmptyChange={setIsEmpty} />
        </AthosTrendingProvider>
        )
      )}
    </div>
  )
}
